import React, { useContext, useMemo } from 'react';
import styles from './ReportSectionSelect.module.scss';
import { SFButton, SFMenu, SFMenuItem, SFMenuOption } from 'sfui';
import { Customer, CustomerContext } from 'ui-smartforce-settings';
import { formatDate } from '../../../../Helpers';
import { ReportSectionSelectOptionItem } from './ReportSectionSelectOptionItem/ReportSectionSelectOptionItem';
import { ReportSectionSelectList } from './ReportSectionSelectList/ReportSectionSelectList';
import { ReportSectionID } from '../FullReport';

export interface ReportSectionSelectOption {
  section_id: ReportSectionID;
  section_name: string;
  incident_date: string;
}

export interface ReportSectionSelectProps {
  options: ReportSectionSelectOption[];
  selected: string;
  onChange: (value: ReportSectionID) => void;
}

export const ReportSectionSelect = ({
  options,
  selected,
  onChange
}: ReportSectionSelectProps): React.ReactElement<ReportSectionSelectProps> => {
  const timezone = (useContext(CustomerContext).customer as Customer).timezone;
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLButtonElement>();

  const reportOptions: SFMenuOption[] = useMemo(() => {
    return options.map((option: ReportSectionSelectOption) => ({
      label: formatDate(option.incident_date, 'L - HH:mm', timezone),
      value: option.section_id,
      item: (
        <ReportSectionSelectOptionItem
          title={option.section_name}
          subTitle={formatDate(option.incident_date, 'L - HH:mm', timezone)}
        />
      )
    }));
  }, [options, timezone]);

  return (
    <div className={styles.reportSectionSelect}>
      <h4 className={styles.label}>
        <span className={styles.count}>{options.length}</span> Included Reports
      </h4>

      <div className={styles.select}>
        <SFButton
          onClick={(e) => {
            setMenuAnchor(e.currentTarget);
            setIsMenuOpen((prev) => !prev);
          }}
          sfColor="grey"
          variant="outlined"
          fullWidth={true}
        >
          See All Reports
        </SFButton>

        <SFMenu
          anchorEl={menuAnchor}
          open={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          getContentAnchorEl={null}
          variant="menu"
          PaperProps={{
            className: styles.menuPaper,
            style: {
              width: menuAnchor ? menuAnchor.offsetWidth : undefined
            }
          }}
          MenuListProps={{
            className: styles.menuList
          }}
        >
          <div className={styles.menuPaperWrapper}>
            <h4 className={styles.label}>{options.length} Included Reports</h4>
            {reportOptions.map((option: SFMenuOption) => (
              <SFMenuItem
                className={styles.menuListItem}
                key={option.value}
                onClick={() => {
                  onChange(option.value as ReportSectionID);
                  setIsMenuOpen(false);
                }}
              >
                {option.item}
              </SFMenuItem>
            ))}
          </div>
        </SFMenu>
      </div>

      <ReportSectionSelectList
        className={styles.optionList}
        options={options}
        selected={selected}
        onClick={(incidentNumber: ReportSectionID) => onChange(incidentNumber)}
      />
    </div>
  );
};
